import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dna, FlaskConical, Calculator, PieChart, InfinityIcon, Filter, X, Users, School, Search, ChevronRight } from 'lucide-react';
import PageTemplate from '../../components/Common/PageTemplate';
import { useTranslation } from 'react-i18next';

const FacultyCard = ({ member, departmentConfig, translations }) => {
    const [imgError, setImgError] = useState(false);
    const placeholderUrl = `https://ui-avatars.com/api/?name=${member.name.replace(" ", "+")}&background=e2e8f0&color=64748b&font-size=0.4&bold=true`;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden transform hover:-translate-y-2 transition-transform duration-300 group"
        >
            <div className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                    <div className="relative">
                        <img
                            src={imgError || !member.photo_url ? placeholderUrl : member.photo_url}
                            alt={member.name}
                            className="w-24 h-24 rounded-full object-cover ring-4 ring-gray-200 group-hover:ring-blue-500 transition-all duration-300"
                            onError={() => setImgError(true)}
                        />
                    </div>
                    <div>
                        {member.is_president && (
                            <div className="mb-2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md inline-block">
                                {translations.chair}
                            </div>
                        )}
                        <p className={`text-sm font-semibold ${departmentConfig[member.department]?.color || 'text-gray-600'}`}>
                            {member.department}
                        </p>
                        <h3 className="text-xl font-bold text-gray-800">{member.name}</h3>
                        <p className="text-gray-500">{member.title}</p>
                    </div>
                </div>
                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600 font-medium bg-gray-100 px-3 py-1 rounded-full inline-block">
                        {member.sub_department}
                    </p>
                </div>
                <div className="mt-6">
                    <a
                        href={member.avesis_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center bg-blue-500 text-white font-semibold py-2 rounded-lg hover:bg-blue-600 transition-colors duration-300"
                    >
                        {translations.avesisProfile}
                    </a>
                </div>
            </div>
        </motion.div>
    );
};

const DepartmentButton = ({ dept, isMobile = false, selectedDept, setSelectedDept, setFilterMenuOpen, departmentConfig }) => {
    const config = departmentConfig[dept] || {};
    const Icon = config.icon || Filter;
    const isActive = selectedDept === dept;
    
    return (
        <button
            onClick={() => {
                setSelectedDept(dept);
                if (isMobile) setFilterMenuOpen(false);
            }}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-all duration-200 ${
                isActive ? `bg-blue-600 text-white shadow-lg` : 'hover:bg-gray-200'
            }`}
        >
            <Icon className={`w-6 h-6 ${isActive ? 'text-white' : config.color || 'text-gray-500'}`} />
            <span className="font-semibold">{dept}</span>
        </button>
    );
};

const SubDepartmentButton = ({ subDept, selectedSubDept, setSelectedSubDept }) => {
    const isActive = selectedSubDept === subDept;
    return (
        <button
            onClick={() => setSelectedSubDept(subDept)}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                isActive ? 'bg-blue-500 text-white shadow' : 'bg-white text-gray-700 hover:bg-gray-100 border'
            }`}
        >
            {subDept}
        </button>
    );
};

const FacultyMembers = () => {
    const { t } = useTranslation();
    const facultyData = t('facultyMembers.facultyData', { returnObjects: true });
    const departmentTranslations = t('facultyMembers.departments', { returnObjects: true });
    const translations = {
        chair: t('facultyMembers.chair'),
        avesisProfile: t('facultyMembers.avesisProfile'),
        departments: t('facultyMembers.departmentsTitle'),
        searchPlaceholder: t('facultyMembers.searchPlaceholder'),
        noResults: t('facultyMembers.noResults'),
        all: t('facultyMembers.all'),
    };

    const departmentConfig = {
        [departmentTranslations.Biology]: { icon: Dna, color: "text-green-500" },
        [departmentTranslations.Chemistry]: { icon: FlaskConical, color: "text-blue-500" },
        [departmentTranslations["Actuarial Sciences"]]: { icon: Calculator, color: "text-purple-500" },
        [departmentTranslations.Statistics]: { icon: PieChart, color: "text-yellow-500" },
        [departmentTranslations.Mathematics]: { icon: InfinityIcon, color: "text-red-500" },
    };

    const departments = useMemo(() => [translations.all, ...Object.keys(departmentConfig)], [translations.all, departmentConfig]);
    const [selectedDept, setSelectedDept] = useState(departments[0]);
    const [selectedSubDept, setSelectedSubDept] = useState(translations.all);
    const [isFilterMenuOpen, setFilterMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const subDepartments = useMemo(() => {
        if (selectedDept === translations.all) return [];
        const uniqueSubDepts = [...new Set(facultyData.filter(m => m.department === selectedDept).map(m => m.sub_department))];
        return [translations.all, ...uniqueSubDepts];
    }, [selectedDept, facultyData, translations.all]);

    useEffect(() => {
        setSelectedSubDept(translations.all);
    }, [selectedDept, translations.all]);

    const filteredFaculty = useMemo(() => {
        return facultyData
            .filter(member => selectedDept === translations.all || member.department === selectedDept)
            .filter(member => selectedSubDept === translations.all || member.sub_department === selectedSubDept)
            .filter(member => member.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [selectedDept, selectedSubDept, searchTerm, facultyData, translations.all]);

    return (
        <PageTemplate
            title={t('pages.facultyMembers.title')}
            subtitle={t('pages.facultyMembers.subtitle')}
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* --- Sidebar --- */}
                <aside className="lg:col-span-3 xl:col-span-2">
                    <div className="sticky top-24">
                        <div className="p-4 bg-white rounded-xl shadow-lg">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">{translations.departments}</h3>
                            <div className="space-y-2">
                                {departments.map(dept => (
                                    <DepartmentButton 
                                        key={dept} 
                                        dept={dept} 
                                        selectedDept={selectedDept}
                                        setSelectedDept={setSelectedDept}
                                        departmentConfig={departmentConfig}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </aside>

                {/* --- Main Content --- */}
                <main className="lg:col-span-9 xl:col-span-10">
                    <div className="sticky top-24 z-10 bg-gray-50/80 backdrop-blur-sm -mx-4 px-4 py-3 mb-6">
                        <div className="bg-white p-4 rounded-xl shadow-md">
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="relative flex-grow">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder={translations.searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                                {subDepartments.length > 1 && (
                                    <div className="flex items-center gap-2 overflow-x-auto pb-2 -mb-2">
                                        {subDepartments.map(subDept => (
                                            <SubDepartmentButton 
                                                key={subDept} 
                                                subDept={subDept} 
                                                selectedSubDept={selectedSubDept}
                                                setSelectedSubDept={setSelectedSubDept}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <AnimatePresence>
                        <motion.div 
                            layout
                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6"
                        >
                            {filteredFaculty.map(member => (
                                <FacultyCard key={`${member.name}-${member.avesis_url}`} member={member} departmentConfig={departmentConfig} translations={translations} />
                            ))}
                        </motion.div>
                    </AnimatePresence>

                    {filteredFaculty.length === 0 && (
                        <div className="text-center py-20">
                            <p className="text-gray-500 text-xl">{translations.noResults}</p>
                        </div>
                    )}
                </main>
            </div>
        </PageTemplate>
    );
};

export default FacultyMembers;
